/**
 * Interpreter mode — the v2 wire contract used by web and ready for RN.
 *
 * WHY THIS LIVES HERE
 * -------------------
 * This contract was hand-ported between clients rather than shared, and the
 * copies drifted: RN migrated to v2 independently on 2026-07-24 while the web
 * client stayed on the v1 event names (`state`/`transcript`/`partial`/…). Both files carried
 * a comment claiming they mirrored each other, which stopped being true the day
 * one of them moved. `grep -rli interpreter packages/` returned zero hits —
 * every other cross-client concern (auth, entitlements, billing types) was
 * already shared; interpreter alone was not.
 *
 * The backend emits four events over Socket.IO:
 *   interpreter:session · interpreter:utterance · interpreter:error · interpreter:suggestion
 *
 * The legacy concierge-in-the-middle set (cultural notes, visual aids, speaker
 * roles, privacy, agent state) is gone from the worker and MUST NOT come back
 * here — a client-side type is not the place to keep a dead contract alive.
 */

// ── View mode ───────────────────────────────────────────────────────────

export type InterpreterViewMode = 'split' | 'flip-user' | 'flip-local';

// ── interpreter:session ─────────────────────────────────────────────────

export type InterpreterSessionStatus =
  | 'starting'
  | 'priming'
  | 'active'
  | 'paused'
  | 'ended'
  | 'error';

export type InterpreterPhase = 'prime' | 'interpret';

export interface InterpreterPair {
  traveler: string;
  local: string | null;
}

export interface InterpreterSessionState {
  state: InterpreterSessionStatus;
  phase: InterpreterPhase;
  pair: InterpreterPair;
  seq: number;
}

// ── interpreter:utterance ───────────────────────────────────────────────

export type InterpreterDirection = 'traveler_to_local' | 'local_to_traveler';

export type InterpreterUtteranceStatus =
  | 'hearing'
  | 'translating'
  | 'speaking'
  | 'done'
  | 'dropped';

export interface InterpreterUtterance {
  id: string;
  /**
   * Assigned ONCE per utterance id and never re-stamped on later revisions.
   *
   * The worker used to call _next_seq() on every emit, so each ~150ms partial
   * gave the same utterance a brand-new (higher) seq — and because clients sort
   * by seq, the in-progress utterance leapt to the bottom of the transcript
   * several times a second while the user was reading it. Fixed backend-side on
   * 2026-08-01; `revision` is what distinguishes repeat emits.
   */
  seq: number;
  direction: InterpreterDirection;
  sourceLang: string;
  targetLang: string;
  sourceText: string;
  targetText: string;
  /** false = live partial (replace-by-id); true = settled */
  final: boolean;
  revision: number;
  status: InterpreterUtteranceStatus;
  /** First time this utterance was observed. Stable across revisions. */
  tStart: number;
  /** Set only on the final emit, so tFinal - tStart is a real duration. */
  tFinal: number | null;
}

// ── interpreter:error ───────────────────────────────────────────────────

export interface InterpreterErrorPayload {
  kind:
    | 'utterance_dropped'
    | 'session_restarting'
    | 'audio_stall'
    | 'interpreter_silent';
  utteranceId: string | null;
  recoverable: boolean;
}

// ── interpreter:suggestion ──────────────────────────────────────────────

export interface InterpreterSuggestionPayload {
  kind: 'language_switch';
  detectedLang: string;
  confidence: number;
}

// ── Reducer state ───────────────────────────────────────────────────────

/**
 * Everything the UI renders, derived only from events. Deliberately plain data:
 * no Map, no Date, no class instances — so a test can build one from JSON, and
 * two clients can agree on it without sharing a rendering stack.
 */
export interface InterpreterState {
  active: boolean;
  session: InterpreterSessionState | null;
  /** Keyed by utterance id; replace-by-id is the update rule. */
  utterances: Record<string, InterpreterUtterance>;
  /** Most recent error, or null once superseded/cleared. */
  lastError: InterpreterErrorPayload | null;
  /** Pending language-switch hint. Rendered as a chip; NEVER auto-applied. */
  suggestion: InterpreterSuggestionPayload | null;
  viewMode: InterpreterViewMode;
}

export type InterpreterEvent =
  | { type: 'session'; payload: InterpreterSessionState }
  | { type: 'utterance'; payload: InterpreterUtterance }
  | { type: 'error'; payload: InterpreterErrorPayload }
  | { type: 'suggestion'; payload: InterpreterSuggestionPayload }
  | { type: 'reset' }
  | { type: 'setViewMode'; payload: InterpreterViewMode }
  | { type: 'dismissSuggestion' };

export const INITIAL_INTERPRETER_STATE: InterpreterState = {
  active: false,
  session: null,
  utterances: {},
  lastError: null,
  suggestion: null,
  viewMode: 'split',
};

/** Session states in which the HUD should be on screen. */
export const LIVE_SESSION_STATES: ReadonlyArray<InterpreterSessionStatus> = [
  'starting',
  'priming',
  'active',
  'paused',
];
